import React, { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Paper, Stack } from '@mui/material';

import { PropertyTextInput, TooltipIconButton } from '@paragon/client-tools';
import { ComponentDesign } from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';

import { setComponentName } from '../../../store/actions/editComponentActions';
import { finishRenamingDesignsAssociatedWithTrussEnvelopes } from '../../../store/actions/layout/trussEnvelopeActions';
import { getCommittedComponentDesigns } from '../../../store/selectors/getComponentDesignsSelectors';
import { setHoveredComponentDesignGuidInList } from '../../../store/slices/selectionsSlice';
import { AutoSizingComponentPreview } from '../../controls/AutoSizingComponentPreview';
import { useAppDispatch, useAppSelector } from '../../hooks';

export function RenameLayoutComponentsPanel() {
  const dispatch = useAppDispatch();

  const state = useAppSelector(({ panelData }) => panelData.renameLayoutComponentsPanel);

  const componentDesigns = useAppSelector(getCommittedComponentDesigns);

  const sceneSelectedComponent = state.open ? state.sceneSelectedComponent : null;

  useEffect(() => {
    if (sceneSelectedComponent?.guid == null) return;

    const input = document.getElementById(getInputId(sceneSelectedComponent.guid));
    if (input == null) return;

    input.focus();
    input.scrollIntoView({ behavior: 'smooth' });
  }, [sceneSelectedComponent]);

  if (state.open === false) return null;

  const componentDesignsBeingRenamed = componentDesigns.filter((design) =>
    state.componentDesignGuids.includes(design.guid),
  );

  return (
    <>
      <Stack direction="row" sx={{ justifyContent: 'flex-end', paddingTop: 1 }}>
        <TooltipIconButton
          title="Close (Esc)"
          onClick={() => void dispatch(finishRenamingDesignsAssociatedWithTrussEnvelopes())}
        >
          <CloseIcon />
        </TooltipIconButton>
      </Stack>

      <PropertiesPanel title="Rename Component Designs" sx={{ margin: 1, display: 'flex', gap: 1 }}>
        {componentDesignsBeingRenamed.map((design) => (
          <RenamableComponentTile key={design.guid} design={design} />
        ))}

        <Button
          fullWidth
          variant="outlined"
          onClick={() => void dispatch(finishRenamingDesignsAssociatedWithTrussEnvelopes())}
        >
          Close
        </Button>
      </PropertiesPanel>
    </>
  );
}

function getInputId(componentDesignGuid: string) {
  return `rename-component-panel-${componentDesignGuid}`;
}

function RenamableComponentTile(props: { design: ComponentDesign }) {
  const dispatch = useAppDispatch();

  const inputRef = useRef<HTMLInputElement | null>(null);

  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (hovered || focused) {
      dispatch(setHoveredComponentDesignGuidInList(props.design.guid));
      return () => void dispatch(setHoveredComponentDesignGuidInList(null));
    }
  }, [dispatch, hovered, focused, props.design.guid]);

  return (
    <Paper
      key={props.design.guid}
      elevation={hovered ? 4 : 1}
      sx={{
        padding: 1,

        gap: 1,
        display: 'flex',
        '& > *': { flex: '1 1 0' },
      }}
      onClick={() => inputRef.current?.focus()}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      <AutoSizingComponentPreview componentDesignGuid={props.design.guid} />

      <div>
        <PropertyTextInput
          property="Name"
          InputProps={{ autoComplete: 'off' }}
          id={getInputId(props.design.guid)}
          inputRef={inputRef}
          value={props.design.component.name}
          submit={(updatedName) => {
            dispatch(setComponentName(updatedName, props.design.guid));
          }}
          onFocus={(event) => {
            setFocused(true);
            event.target.select();
          }}
          onBlur={() => {
            setFocused(false);
          }}
        />
      </div>
    </Paper>
  );
}
